/**
 * Generator 10: Edit page — (authenticated)/<n>/[id]/edit/page.tsx
 * Generates server page component for editing an existing entity
 */

import type { GeneratedFile, GeneratorOptions, Schema } from '@core-engine/schema/types.js'
import { createGeneratedFile } from '@core-engine/utils/generated-files.js'
import { singularize, toCamelCase, toKebabCase, toPascalCase } from '@core-engine/utils/string.js'
import { resolveSchemaActionImportPath } from '@next-adapter/config/paths.js'

// ============================================================================
// Public API
// ============================================================================

export interface EditPageGeneratorResult {
  files: GeneratedFile[]
}

/**
 * Generate edit page component for updating an existing entity
 */
export function generateEditPage(
  schema: Schema,
  pagesDir: string,
  options: GeneratorOptions = {}
): EditPageGeneratorResult {
  const singular = singularize(schema.name)
  const Singular = toPascalCase(singular)
  const camelSingular = toCamelCase(singular)
  const kebabName = toKebabCase(schema.name)
  const actionImportPath = resolveSchemaActionImportPath(schema.name, options.outputNamespace)

  const content = `import { notFound } from 'next/navigation'
import { get${Singular}ById } from '@admin/actions/${actionImportPath}'
import { ${Singular}Form } from '../../${kebabName}-form'

interface PageProps {
  params: Promise<{
    id: string
  }>
}

export default async function Edit${Singular}Page({ params }: PageProps) {
  const { id } = await params
  const ${camelSingular} = await get${Singular}ById(id)

  if (!${camelSingular}) {
    notFound()
  }

  return <${Singular}Form key={\`\${${camelSingular}.id}-\${${camelSingular}.updatedAt}\`} initialData={${camelSingular}} pageTitle="Edit ${Singular}" />
}
`

  return {
    files: [createGeneratedFile(`${pagesDir}/${schema.name}/[id]/edit/page.tsx`, content)]
  }
}
